import { useEffect, useState } from 'react'
import { Box } from 'components/elements'
import { DownArrowIcon, UpArrowIcon, RightArrowIcon } from 'components/icons'
import { IconButton } from 'components/button'
import { styled } from 'styles'
import { useIncidents } from '../../api/getIncidents'
import { IncidentEnums } from '../enums'
import { ClaimColor, formatLocalDate, formatLocalTime } from '../../../../utils/function'

import { EmptyRecord, MainCard, OverflowScroll, StyledTable, StyledRow, ButtonCellCSS, ExpendedCSS, StyledStatusBox } from '../style'

import { useMediaQuery } from 'react-responsive'
import { GetIncidentsParams, PageProps } from 'features/quote/components/type'
import Pagination from 'components/Pagination'
import { LinearProgress } from '@mui/material'
import IncidentsDetails from './IncidentsDetails'
import { RowProps } from '../type'
import { TERMINOLOGY } from 'utils/enum'

const Row = ({ row, open, onExpand, isDesktopOrLaptop, isMobileOrTablet, isPrimaryService }: RowProps) => {
  const isPotentialClaim = row?.isPotentialClaim || false
  const createdAt = row?.threatCreatedAt ?? row?.createdAt

  return (
    <>
      <StyledRow className={`${open ? 'expanded--bg-color' : undefined} ${isPotentialClaim ? 'warningBg--only' : ''}`}>
        {isDesktopOrLaptop && (
          <StyledCell css={{ p: 0, pl: '10px' }}>
            <IconButton onClick={onExpand}>{open ? <UpArrowIcon /> : <DownArrowIcon />}</IconButton>
          </StyledCell>
        )}
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              Date
            </Box>
          )}
          {formatLocalDate(createdAt)}
        </StyledCell>
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              Time
            </Box>
          )}
          {formatLocalTime(createdAt)}
        </StyledCell>
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              Device Name
            </Box>
          )}
          {row?.['Agent Realtime Info']?.['Agent Computer Name'] || '_'}
        </StyledCell>
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              Threat Name
            </Box>
          )}
          {row[IncidentEnums.THREAT_INFO]?.[IncidentEnums.THREAT_NAME] || '-'}
        </StyledCell>
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              Claim Status
            </Box>
          )}
          <StyledStatusBox as="p" css={{ margin: 0 }} className={`${ClaimColor(row?.insuranceDetail?.claimStatus)}`}>
            {row?.insuranceDetail?.claimStatus || '_'}
          </StyledStatusBox>
        </StyledCell>
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              {TERMINOLOGY.partner.singular}
            </Box>
          )}
          {row?.companyName || '_'}
        </StyledCell>
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              {TERMINOLOGY.warranty.singular}
            </Box>
          )}
          {row?.planName || '_'}
        </StyledCell>
        <StyledCell>
          {!isDesktopOrLaptop && (
            <Box as="h4" css={{ mb: 0 }}>
              Device ID{' '}
            </Box>
          )}
          {row?.['Agent Realtime Info']?.['Agent Id'] || '_'}
        </StyledCell>
        {!isDesktopOrLaptop && (
          <StyledCell css={{ p: 0 }}>
            <IconButton css={ButtonCellCSS} className="detail--view" onClick={onExpand}>
              <Box as="h4" css={{ m: '0 1rem 0 0', color: '$primary' }}>
                View Details
              </Box>
              <RightArrowIcon />
            </IconButton>
          </StyledCell>
        )}
      </StyledRow>

      <StyledRow className={open ? 'expanded' : undefined} css={ExpendedCSS}>
        {open && (
          <StyledCell colSpan={20} css={{ p: 0 }}>
            <IncidentsDetails row={row} onExpand={onExpand} isDesktopOrLaptop={isDesktopOrLaptop} isMobileOrTablet={isMobileOrTablet} isPrimaryService={isPrimaryService} />
          </StyledCell>
        )}
      </StyledRow>
    </>
  )
}

export const IncidentsTable = ({ setCount, claimStatus, isPrimaryService }: PageProps) => {
  const [openId, setOpenId] = useState(-1)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const [params, setParams] = useState<GetIncidentsParams>({
    pageNumber: page,
    pageSize: rowsPerPage,
    claimStatus: claimStatus || null,
  })
  const { data, isLoading } = useIncidents(params)

  useEffect(() => {
    if (setCount) setCount(data?.data.total)
    // eslint-disable-next-line
  }, [data])

  useEffect(() => {
    setParams({
      pageNumber: page,
      pageSize: rowsPerPage,
      claimStatus: claimStatus || null,
    })
  }, [claimStatus, page, rowsPerPage])

  useEffect(() => {
    setPage(0)
    setOpenId(-1)
  }, [claimStatus])

  const doc: Document = document
  const body = doc.querySelector('body')
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 991 })
  const isMobileOrTablet = useMediaQuery({ minWidth: 767 })

  const incidentsData = data?.data.fields || []
  return (
    <MainCard>
      <Box css={OverflowScroll}>
        <StyledTable css={{ marginBottom: '1rem' }} className={`${!isDesktopOrLaptop && incidentsData.length ? 'table--mobile' : 'responsive-mobile'} `}>
          {isDesktopOrLaptop && (
            <thead>
              <StyledRow>
                <StyledCell as="th"></StyledCell>
                <StyledCell as="th" className="minWidth-175">
                  Date
                </StyledCell>
                <StyledCell as="th" className="minWidth-175">
                  Time
                </StyledCell>
                <StyledCell as="th" className="minWidth-175">
                  Device Name
                </StyledCell>
                <StyledCell as="th" className="minWidth-200">
                  Threat Name
                </StyledCell>
                <StyledCell as="th" className="minWidth-175">
                  Claim Status
                </StyledCell>
                <StyledCell as="th" className="minWidth-175">
                  {TERMINOLOGY.partner.singular}
                </StyledCell>
                <StyledCell as="th" className="minWidth-175">
                  {TERMINOLOGY.warranty.singular}
                </StyledCell>
                <StyledCell as="th" className="minWidth-175">
                  Device ID
                </StyledCell>
              </StyledRow>
            </thead>
          )}
          <tbody>
            {isLoading ? (
              <th colSpan={9}>
                <LinearProgress sx={{ height: '6px' }} />
              </th>
            ) : (
              <>
                {incidentsData.length ? (
                  incidentsData.map((row: Record<string, any>, idx: number) => (
                    <Row
                      key={idx}
                      row={row}
                      open={openId === idx}
                      id={idx}
                      onExpand={() => {
                        setOpenId(prev => (prev === idx ? -1 : idx))
                        openId === -1 ? body?.classList.add('overflow--hidden') : body?.classList.remove('overflow--hidden')
                      }}
                      isDesktopOrLaptop={isDesktopOrLaptop}
                      isMobileOrTablet={isMobileOrTablet}
                      isPrimaryService={isPrimaryService}
                    />
                  ))
                ) : (
                  <EmptyRecord colSpan={9}>No Data</EmptyRecord>
                )}
              </>
            )}
          </tbody>
        </StyledTable>
      </Box>

      <Pagination count={data?.data?.total || 0} rowsPerPage={rowsPerPage} page={page} setRowsPerPage={setRowsPerPage} setPage={setPage} setOpenId={setOpenId} />
    </MainCard>
  )
}

export const StyledCell = styled('td', {
  'textAlign': 'left',
  'padding': '1.25rem',

  '& > .status': {
    p: '2px 10px 6px',
    borderRadius: 8,
    textAlign: 'center',
  },
})

export const flexWrapCss = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  flexWrap: 'wrap',
}
